import "./styles.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";

import { ToastProvider } from "./components/overlay";
import { Shell } from "./components/Shell";
import { initLocale, isLocale, locale, setLocale, t, useLocale } from "./i18n";
import { Consent, OAuthError } from "./screens/Consent";
import { ForgotPassword, Invitation, MagicLink, ResetPassword, SignIn, SignUp, VerifyEmail } from "./screens/Auth";
import { Home } from "./screens/Home";
import { useEffect } from "react";
import { useMe } from "./session";
import { applyTheme, readTheme } from "./theme";

// The help centre is its own chunk: it carries the articles.
const Help = lazy(() => import("./help/Help"));
// Every other screen loads when it is first opened (T-491): Home and sign-in stay in the
// first download.
const AssetPage = lazy(() => import("./screens/AssetPage").then((m) => ({ default: m.AssetPage })));
const Search = lazy(() => import("./screens/Search").then((m) => ({ default: m.Search })));
const Freshness = lazy(() => import("./screens/Freshness").then((m) => ({ default: m.Freshness })));
const Glossary = lazy(() => import("./screens/Glossary").then((m) => ({ default: m.Glossary })));
const LanguageCoverage = lazy(() => import("./screens/LanguageCoverage").then((m) => ({ default: m.LanguageCoverage })));
const FlowEditor = lazy(() => import("./screens/FlowEditor").then((m) => ({ default: m.FlowEditor })));
const FeedbackPage = lazy(() => import("./screens/Feedback").then((m) => ({ default: m.FeedbackPage })));
const Ops = lazy(() => import("./screens/Ops").then((m) => ({ default: m.Ops })));
const Scripts = lazy(() => import("./screens/Scripts").then((m) => ({ default: m.Scripts })));
const ScriptEditor = lazy(() => import("./screens/Scripts").then((m) => ({ default: m.ScriptEditor })));
const Assets = lazy(() => import("./screens/Assets").then((m) => ({ default: m.Assets })));
const Demos = lazy(() => import("./screens/Demos").then((m) => ({ default: m.Demos })));
const EmailTemplates = lazy(() => import("./screens/EmailTemplates").then((m) => ({ default: m.EmailTemplates })));
const Composer = lazy(() => import("./screens/Demos").then((m) => ({ default: m.Composer })));
const DemoPage = lazy(() => import("./screens/Demos").then((m) => ({ default: m.DemoPage })));
const Recorder = lazy(() => import("./screens/Recorder").then((m) => ({ default: m.Recorder })));
const Insights = lazy(() => import("./screens/Insights").then((m) => ({ default: m.Insights })));
const Stakeholders = lazy(() => import("./screens/Stakeholders").then((m) => ({ default: m.Stakeholders })));
const Journey = lazy(() => import("./screens/Journey").then((m) => ({ default: m.Journey })));
const MyAccount = lazy(() => import("./screens/Sections").then((m) => ({ default: m.MyAccount })));
const Requests = lazy(() => import("./screens/Requests").then((m) => ({ default: m.Requests })));
const Settings = lazy(() => import("./screens/Settings").then((m) => ({ default: m.Settings })));

applyTheme(readTheme());

// Only same-origin paths are followed after sign-in, never another site.
export function safeNext(value: string | null): string {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : "/";
}

function AfterSignIn() {
  const next = safeNext(new URLSearchParams(window.location.search).get("next"));
  return <Navigate to={next} replace />;
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

function App() {
  const me = useMe();
  const current = useLocale();
  // Signed in, the account's language wins over the device's.
  const accountLocale = me.data?.locale;
  useEffect(() => {
    if (isLocale(accountLocale) && accountLocale !== locale()) void setLocale(accountLocale);
  }, [accountLocale]);
  if (me.isLoading) {
    return <div className="grid min-h-full place-items-center text-steel">{t("common.working")}</div>;
  }
  if (me.isError) {
    return <div className="grid min-h-full place-items-center text-err">{t("common.error")}</div>;
  }
  const user = me.data ?? null;

  return (
    <Routes key={current}>
      <Route path="/sign-in" element={user ? <AfterSignIn /> : <SignIn />} />
      <Route path="/sign-up" element={user ? <Navigate to="/" replace /> : <SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/magic-link" element={user ? <Navigate to="/" replace /> : <MagicLink />} />
      <Route path="/consent" element={<Consent me={user} />} />
      <Route path="/oauth-error" element={<OAuthError />} />
      <Route path="/invitation" element={<Invitation me={user} />} />
      <Route path="/help" element={<Suspense fallback={null}><Help signedIn={!!user} /></Suspense>} />
      <Route path="/help/:slug" element={<Suspense fallback={null}><Help signedIn={!!user} /></Suspense>} />
      {user ? (
        <Route element={<Shell me={user} />}>
          <Route index element={<Home me={user} />} />
          <Route path="/assets" element={<Assets />} />
          <Route path="/assets/:id" element={<AssetPage />} />
          <Route path="/assets/:id/flow" element={<FlowEditor />} />
          <Route path="/search" element={<Search />} />
          <Route path="/freshness" element={<Freshness />} />
          <Route path="/glossary" element={<Glossary me={user} />} />
          <Route path="/languages" element={<LanguageCoverage />} />
          <Route path="/record" element={<Recorder />} />
          <Route path="/demos" element={<Demos me={user} />} />
          <Route path="/demos/new" element={<Composer />} />
          <Route path="/demos/templates" element={<EmailTemplates />} />
          <Route path="/demos/:id" element={<DemoPage me={user} />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/accounts/:key" element={<Stakeholders />} />
          <Route path="/insights/accounts/:key/people/:person" element={<Journey />} />
          <Route path="/settings" element={<Settings me={user} />} />
          <Route path="/account" element={<MyAccount me={user} />} />
          <Route path="/ops" element={<Ops />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/scripts" element={<Scripts />} />
          <Route path="/requests" element={<Requests me={user} />} />
          <Route path="/scripts/:id" element={<ScriptEditor />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      )}
    </Routes>
  );
}

const root = document.getElementById("root");
if (root) {
  await initLocale();
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
